// filepath: apps/web/src/features/lists/kanban/hooks/useKanbanRecords.ts
//
// FR-UI-VIEW-1 AC-1 / AC-2 / AC-3 — TanStack Query hook for the Kanban
// view's record fetch.
//
// The Kanban renderer issues GET /api/entity-records/{entityType}
// against the same FR-QRY-1 endpoint the EntityListTable uses
// (apps/web/src/features/lists/hooks/useEntityList.ts). The cache key
// shape differs intentionally so the Kanban window cannot accidentally
// mutate the list-view cache (and vice versa):
//
//   list  : ["list", tenantSlug, entityType, filterHash, sortKey, cursor]
//   kanban: ["lists", tenantSlug, "kanban", entityType, viewConfigVersion]
//
// The Kanban window ignores URL-derived filter/sort/cursor state — the
// table is the URL-driven surface. The Kanban window shows ALL records
// the caller may read (FR-UI-VIEW-1 AC-1 — drag-drop into a column is a
// group-by update, not a row-targeted query). The server still
// applies CanRead field stripping (INV-2 / FR-AUTH-7): the renderer
// iterates `Object.keys(record.fields)` and renders exactly what the
// server returned.
//
// SECURITY / INTEGRITY INVARIANTS:
//   - INV-1 — tenantSlug is the 2nd segment of the cache key tuple.
//   - INV-2 — the hook does NOT do per-row fetches to "fill in" server-
//     stripped fields. The response shape is authoritative.
//   - The 500-of-600 cap is server-enforced (FR-UI-VIEW-1 AC-3);
//     the hook surfaces BOTH `records` and a derived `total` so the
//     KanbanCountBanner can render the "X of Y records shown." message
//     when the server caps the result set. Total comes from the wire
//     shape when present, falling back to the records.length so a
//     server older than the banner-cap deployment degrades cleanly
//     (no banner; OFF by default).

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "@/definitions/types";
import { useTenant } from "@/tenant/TenantProvider";
import { apiFetch } from "@/api";
import type { ListResponseWire, EntityRecordWire } from "../../types";
import type { KanbanRecord } from "../KanbanCard";

/**
 * Hard cap the server enforces on the Kanban window. Mirrors the
 * "500-of-600" rendering rule at FR-UI-VIEW-1 AC-3 — the cap is
 * server-enforced so the SPA doesn't need to second-guess the result
 * size. Surfaced as a constant for tests + the KanbanCountBanner so
 * a future FR bumping the cap has a single source of truth.
 */
export const KANBAN_RECORDS_CAP = 500;

/**
 * TanStack Query cache key factory for the Kanban window.
 *
 * Shape: `["lists", tenantSlug, "kanban", entityType, viewConfigVersion]`.
 *
 * - 1st segment `"lists"` is the feature namespace so other list-feature
 *   hooks (useEntityList) don't collide.
 * - 2nd segment `tenantSlug` is the INV-1 invariant — switching
 *   tenants resolves to a distinct cache entry.
 * - 3rd segment `"kanban"` disambiguates the Kanban window from any
 *   future list-feature view (table / tree / gantt).
 * - 4th segment `entityType` is the entity-type filter.
 * - 5th segment `viewConfigVersion` is the entity definition's
 *   `version` number — when a definition is republished the cache
 *   entry is invalidated automatically (the parent component holds
 *   the `version` and passes it as an explicit argument).
 */
export const kanbanKeys = {
  all: ["lists"] as const,
  window: (tenantSlug: string, entityType: string, viewConfigVersion: number) =>
    [
      "lists",
      tenantSlug,
      "kanban",
      entityType,
      String(viewConfigVersion),
    ] as const,
};

export interface UseKanbanRecordsOptions {
  /** Target entity type id. Required. */
  entityType: string;
  /**
   * The entity-definition version that supplied the `view_config`
   * block. When this changes the cache key changes automatically,
   * so a definition re-publication refreshes the Kanban view (the
   * group_by field may differ between versions).
   */
  viewConfigVersion: number;
  /** API base URL override. Defaults to `/api`. */
  apiBase?: string;
  /**
   * When false, the query is disabled (no fetch). Used when the
   * definition is still loading or the entityType is missing.
   */
  enabled?: boolean;
}

export interface KanbanEnvelope {
  /**
   * Normalised records the renderer should display. Each record is
   * projected into `KanbanRecord` (extends NormalisedRecord with the
   * `rowVersion` field) so the drag-drop handlers can supply the
   * OCC token without a follow-up fetch (INV-7). The rowVersion is
   * read from the server-emitted record; when the wire omits it the
   * record is dropped (cannot be moved safely).
   */
  records: KanbanRecord[];
  /**
   * Server-supplied total record count across all columns. Null
   * when the server wire omits the additive `total` field (older
   * deployments). When null, the KanbanCountBanner stays off.
   */
  total: number | null;
}

export interface UseKanbanRecordsResult {
  /** Kanban envelope. `undefined` while loading or before first success. */
  data: KanbanEnvelope | undefined;
  /** True while the initial fetch is in flight. */
  isLoading: boolean;
  /** True while any fetch (including background refetch) is in flight. */
  isFetching: boolean;
  /** Last error, classified as an `ApiError` on non-2xx responses. */
  error: ApiError | null;
  /** Force refetch (FR-UI-CORE-10 retry). */
  refetch: () => Promise<unknown>;
}

/**
 * Normalise a ListResponseWire into the Kanban-friendly shape.
 *
 * Reads the additive `total` field when present (FR-QRY-2 wire shape
 * extension for FR-UI-VIEW-1 AC-3). When absent, `total` is null and
 * the KanbanCountBanner stays off.
 *
 * The server still applies CanRead field stripping on every record
 * (INV-2 / FR-AUTH-7) — the renderer MUST iterate only
 * `Object.keys(record.fields)` and never fall back to a per-row GET
 * to "fill in" absent fields.
 */
function normaliseKanbanPage(wire: ListResponseWire): KanbanEnvelope {
  const records: KanbanRecord[] = (wire.records ?? [])
    .map((r: EntityRecordWire): KanbanRecord | null => {
      // INV-7 — the record must carry its row_version for drag-drop.
      // Older wire envelopes may omit the additive `row_version`; we
      // drop those records rather than emit an unverifiable token.
      // The server still applies CanRead field stripping on the
      // surviving records (INV-2 / FR-AUTH-7).
      const rawVersion = (r as unknown as { row_version?: unknown })
        .row_version;
      const rowVersion =
        typeof rawVersion === "number"
          ? rawVersion
          : typeof rawVersion === "string" && /^[0-9]+$/.test(rawVersion)
            ? Number.parseInt(rawVersion, 10)
            : null;
      if (rowVersion === null) return null;
      return {
        id: r.ID,
        fields: (r.Fields ?? {}) as Record<string, unknown>,
        rowVersion,
      };
    })
    .filter((rec): rec is KanbanRecord => rec !== null);
  const total = readTotal(wire);
  return { records, total };
}

/**
 * Read the optional `total` field from a ListResponse envelope.
 *
 * FR-QRY-1 v1 ships `next_cursor` + `page_size` only. The Kanban
 * window's 500-of-N banner needs a server-supplied `total`, which
 * is an additive wire field (the future FR-QRY-2 will deliver it
 * as `total_count`). For backward-compat the helper returns `null`
 * when the field is absent — the hook degrades to records.length so
 * the banner stays off.
 */
function readTotal(wire: ListResponseWire): number | null {
  const raw = (wire as unknown as { total?: unknown }).total;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && /^[0-9]+$/.test(raw)) {
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * useKanbanRecords — TanStack Query hook backing the Kanban window.
 *
 * The hook is "always-enabled" once the parent has a non-empty
 * `entityType` and `viewConfigVersion`. Passing `enabled: false`
 * suppresses the fetch (used when the Kanban window should not be
 * drawn at all — e.g. while the parent's loading skeleton is visible).
 */
export function useKanbanRecords(
  opts: UseKanbanRecordsOptions,
): UseKanbanRecordsResult {
  const {
    entityType,
    viewConfigVersion,
    apiBase = "/api",
    enabled = true,
  } = opts;

  const { tenantSlug } = useTenant();

  const queryKey = kanbanKeys.window(tenantSlug, entityType, viewConfigVersion);

  const query: UseQueryResult<KanbanEnvelope, ApiError> = useQuery<
    KanbanEnvelope,
    ApiError
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      // Kanban cap is server-enforced; we hint to the server that
      // we want the cap applied via the standard `limit` param.
      // The SPA never requests more than KANBAN_RECORDS_CAP on its
      // own (limits the blast radius of a misconfigured tenant).
      params.set("limit", String(KANBAN_RECORDS_CAP));
      const url = `${apiBase}/entity-records/${encodeURIComponent(
        entityType,
      )}?${params.toString()}`;

      let response: Response;
      try {
        // ISS-377 round 3 R2-B2.5: migrated to apiFetch. The
        // AbortSignal is forwarded via `init.signal` so TanStack
        // Query's unmount-cancellation reaches the underlying
        // fetch.
        response = await apiFetch(url, {
          method: "GET",
          init: {
            headers: {
              Accept: "application/json",
            },
            signal,
          },
        });
      } catch (networkErr) {
        throw new ApiError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
          "network_error",
        );
      }

      if (!response.ok) {
        const body = await safeReadJson(response);
        const code =
          (body as { code?: string } | null)?.code ?? `http_${response.status}`;
        const retryAfter = parseRetryAfter(response.headers.get("Retry-After"));
        throw new ApiError(
          (body as { error?: string } | null)?.error ?? response.statusText,
          response.status,
          code,
          retryAfter,
        );
      }

      const data = (await response.json()) as ListResponseWire;
      return normaliseKanbanPage(data);
    },
    enabled: enabled && entityType.length > 0,
    // FR-UI-VIEW-1 AC-6: drag-drop moves trigger explicit query
    // invalidation; staleTime stays short so a refetched record
    // count picks up concurrent edits from other windows within a
    // few seconds.
    staleTime: 5_000,
    gcTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: async () => {
      await query.refetch();
    },
  };
}

// ----------------- tiny helpers (local — not exported) ---------------

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

function parseRetryAfter(raw: string | null): number | null {
  if (!raw) return null;
  const seconds = Number.parseInt(raw, 10);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;
  return null;
}
